import { createSignal } from "solid-js";
import { reconcile } from "solid-js/store";

import { EditUserInfo, getUserInfoByUserID } from "@/api/clientApi/user";
import { message } from "@/components/Base/Message/Message.tsx";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { APP_ID } from "@/constants/constants";
import { useLogin } from "@/hooks/login/useLogin";
import { t } from "@/i18n";
import { formatToBindPoint } from "@/lib/pointUtils";
import { pointToBind } from "@/stores/cryptoStore";
import { profileInfo, setIsEdit, setProfileStore } from "@/stores/profileStore";
import { sysConfigStore } from "@/stores/sysConfigStore.ts";
import {
  getUserInfo,
  headerInfo,
  userStore,
  validateHeaderInfo,
} from "@/stores/userStore.ts";
import { GetUserInfoByUserIDRsp } from "@/types/swagger/front";

import { useSettings } from "./useSettings";

export function useProfile() {
  // loading
  const [editLoading, setEditLoading] = createSignal<boolean>(false);
  const { showDialog, closeDialog } = useDialog();

  // 打开个人资料
  const { toLoginOrRegister } = useLogin();

  const openProfiles = (userId: number, appId?: number, vipLevel?: number) => {
    const callBack = toLoginOrRegister();
    if (callBack) {
      setProfileStore("userId", userId);
      if (userId == userStore.userInfo?.uid) {
        setIsEdit(true);
      } else {
        setIsEdit(false);
      }
      showDialog({
        type: DialogType.Profile,
        data: { userId: userId, appId: appId, vipLevel: vipLevel },
      });
    }
  };
  // 编辑个人信息
  const handleEdit = () => {
    showDialog({ type: DialogType.EditProfile });
  };

  // 编辑提交
  const handleSubmit = async () => {
    const { getUserInfo: getSettingsUserInfo } = useSettings();

    if (editLoading()) return;
    setEditLoading(true);
    validateHeaderInfo("nickname", headerInfo.nickname);
    if (headerInfo.error) return;
    const params = {
      headimg: headerInfo.headimg,
      nickname: headerInfo.nickname,
    };
    await EditUserInfo(params)
      .then(() => {
        message.show(t("tips.saved_successfully"));
        getUserInfo();
        getUserAllInfo(userStore?.userInfo?.uid ?? 0);
        getSettingsUserInfo();
        closeDialog(DialogType.EditProfile);
        setEditLoading(false);
      })
      .catch(() => {
        setEditLoading(false);
      });
  };

  /**
   * 获取用户信息
   *
   * @param userId 用户ID
   */
  const getUserAllInfo = async (userId: number, appId?: number) => {
    setProfileStore("profileInfo", reconcile({} as GetUserInfoByUserIDRsp));
    const params = {
      user_id: userId,
      app_id: appId ?? APP_ID,
    };
    const res = await getUserInfoByUserID(params);
    if (res) {
      setProfileStore("profileInfo", reconcile(res));
    }
  };

  return {
    openProfiles,
    handleEdit,
    handleSubmit,
    getUserAllInfo,
    editLoading,
    setEditLoading,
  };
}

interface StatisticList {
  name: string;
  value: string;
  nameI8n?: string;
}

export const statisticList = (): StatisticList[] => {
  const gameData = profileInfo()?.game_data;
  const list: StatisticList[] = [
    {
      name: "profile.total_bets",
      value: String(gameData?.total_bets ?? 0),
    },
    {
      name: "profile.number_of_wins",
      value: String(gameData?.wins_num ?? 0),
    },
    {
      name: "profile.number_of_losses",
      value: String(gameData?.loss_num ?? 0),
    },
    {
      name: "profile.total_wagered",
      value: formatToBindPoint({
        value: pointToBind(gameData?.wagered ?? 0, sysConfigStore.point_type),
        pointType: sysConfigStore.point_type,
        isConverted: true,
        precision: 2,
      }),
    },
  ];
  return list;
};
